/* eslint-disable max-lines */
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Radio,
    RadioGroup,
    Spinner,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { ComponentProps } from 'react';
import { startOfTomorrow } from 'date-fns';
import { KeyAvailableType, PropertyKeyAvailableType } from '@acp/shared';
import { DatePicker, RestrictedLengthTextAreaField, ThemeTokens } from '@acp/core-ui';
import { EditPropertyFormValues } from './form-schema';
import { useProperty, usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { PropertyAddressCard } from '../property-address-card';
import { PropertyManagerSelect } from '../../../agency';

function EditPropertyScreen({
    propertyId,
    onClose,
    onSubmit,
    showHeader = true,
    showCloseButton = true,
    p,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
}: {
    propertyId: number;
    onClose: () => void;
    onSubmit: (vals: EditPropertyFormValues) => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
}) {
    const { data: propertyData, isLoading, isPending } = useProperty(propertyId);
    const propertyPickupLocation = usePropertyKeyPickupLocation(propertyId);

    const {
        handleSubmit,
        watch,
        control,
        formState: { isDirty },
    } = useFormContext<EditPropertyFormValues>();

    const currentFormValues = watch();
    return (
        <VStack align="left">
            { showHeader && <ModalHeader>Edit property</ModalHeader> }
            <ModalBody p={p}>
                { showHeader && <PropertyAddressCard w="full" mb={2} propertyId={propertyId} /> }
                {/* loading data */}
                <Box w="full">
                    {(isLoading
                        || propertyData === undefined) && (
                        <Flex
                            alignItems="center"
                            fontWeight="bold"
                            justifyContent="center"
                            py={8}
                            px={8}
                            borderColor="muted.50"
                            borderWidth={1}
                            rounded="md"
                        >
                            <Spinner />
                        </Flex>
                    )}
                </Box>

                {showAgencyKeyFields
                && (
                    <VStack gap={2} pb={4}>
                        <Controller
                            name="agencyKey.propertyManagerId"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl
                                    isRequired
                                    isInvalid={fieldState.invalid}
                                    mb={2}
                                >
                                    <FormLabel>Property manager</FormLabel>
                                    <PropertyManagerSelect
                                        onChange={(manager) => {
                                            field.onChange(manager?.value);
                                        }}
                                        value={field.value}
                                        isClearable={false}
                                    />

                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />

                        <Box w="full" p={6} borderColor={ThemeTokens.Defaults.borderColor} borderWidth={1} borderRadius="md">
                            <VStack gap={4}>
                                <Controller
                                    control={control}
                                    name="agencyKey.isKeyAvailable"
                                    render={({ field, fieldState }) => (
                                        <FormControl
                                            isInvalid={fieldState.invalid}
                                            mb={2}
                                        >
                                            <FormLabel htmlFor="agencyKey.isKeyAvailable">Is key cut & available to collect from the agency?</FormLabel>
                                            <RadioGroup {...field} isDisabled={field.disabled}>
                                                <Stack direction="row" spacing={4}>
                                                    <Radio value={KeyAvailableType.Yes}>
                                                        Yes
                                                    </Radio>
                                                    <Radio value={KeyAvailableType.No}>
                                                        No
                                                    </Radio>
                                                </Stack>
                                            </RadioGroup>

                                            {field.value === KeyAvailableType.No && (
                                                <FormHelperText>
                                                    There will no longer be a key
                                                    assigned to this property
                                                </FormHelperText>
                                            )}

                                            <FormErrorMessage>
                                                { fieldState.error && fieldState.error?.message}
                                            </FormErrorMessage>
                                        </FormControl>
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="agencyKey.keyNumber"
                                    render={({ fieldState, field }) => (
                                        <FormControl isInvalid={fieldState.invalid} mb={2}>
                                            <FormLabel>Key number</FormLabel>
                                            <Input {...field} value={field.value as string} placeholder="Key number" />

                                            {fieldState.error && (
                                                <FormErrorMessage>
                                                    {fieldState.error.message}
                                                </FormErrorMessage>
                                            )}

                                            {currentFormValues.agencyKey?.isKeyAvailable
                                                === KeyAvailableType.Yes
                                                && propertyData?.keyLocationId === null
                                                && (
                                                    <FormHelperText mb={2}>
                                                        This key can be picked up at this
                                                        agency office
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    )}
                                />
                                {
                                    currentFormValues.agencyKey?.isKeyAvailable
                                    === KeyAvailableType.Yes
                                && (
                                    <Box w="full" mb={2}>
                                        <Text fontWeight="medium" fontSize="md">
                                            Key pickup location
                                        </Text>
                                        {propertyPickupLocation.data?.fullAddress}
                                    </Box>
                                )
                                }
                            </VStack>
                        </Box>
                    </VStack>
                )}

                {showOnsiteKeyFields
                && (
                    <Box
                        w="full"
                        p={6}
                        borderColor={ThemeTokens.Defaults.borderColor}
                        borderWidth={1}
                        borderRadius="md"
                    >
                        <VStack gap={4}>
                            <Controller
                                control={control}
                                name="propertyKey.onsiteAvailability"
                                render={({ field, fieldState }) => (
                                    <FormControl isRequired isInvalid={fieldState.invalid} mb={2}>
                                        <FormLabel>Is a key onsite at the property?</FormLabel>

                                        <RadioGroup {...field}>
                                            <Stack direction="row" spacing={6}>
                                                <Radio value={PropertyKeyAvailableType.Available}>
                                                    Yes
                                                </Radio>
                                                <Radio value={PropertyKeyAvailableType.Unavailable}>
                                                    No
                                                </Radio>
                                                <Radio value={PropertyKeyAvailableType.Temporary}>
                                                    Temporarily
                                                </Radio>
                                            </Stack>
                                        </RadioGroup>
                                        <FormErrorMessage>
                                            {fieldState.error?.message}
                                        </FormErrorMessage>
                                    </FormControl>

                                )}
                            />
                            {
                                currentFormValues.propertyKey?.onsiteAvailability
                                        === PropertyKeyAvailableType.Temporary
                                    && (
                                        <Controller
                                            control={control}
                                            name="propertyKey.temporaryKeyExpirationDate"
                                            render={({ field, fieldState }) => (
                                                <FormControl
                                                    isInvalid={fieldState.invalid}
                                                    isRequired
                                                    mb={2}
                                                >
                                                    <FormLabel>Expiration date</FormLabel>
                                                    <DatePicker
                                                        {...field}
                                                        placeholder="Select key expiration date"
                                                        isInvalid={fieldState.invalid}
                                                        dateDisplayFormat="do MMMM yyyy"
                                                        allowClear={false}
                                                        fromDate={startOfTomorrow()}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldState.error?.message}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            )}
                                        />
                                    )
                            }
                            {
                                currentFormValues.propertyKey?.onsiteAvailability
                                        !== PropertyKeyAvailableType.Unavailable
                                        && (
                                            <Controller
                                                control={control}
                                                name="propertyKey.keyOnsiteNotes"
                                                render={({ fieldState }) => (
                                                    <FormControl
                                                        isInvalid={fieldState.invalid}
                                                        mb={2}
                                                    >
                                                        <RestrictedLengthTextAreaField
                                                            control={control}
                                                            name="propertyKey.keyOnsiteNotes"
                                                            label="Key onsite notes"
                                                            placeholder="Please explain how we can access this key onsite"
                                                            maxLength={500}
                                                            isRequired
                                                        />
                                                    </FormControl>
                                                )}
                                            />
                                        )
                            }
                        </VStack>
                    </Box>
                )}
            </ModalBody>
            <ModalFooter p={p} justifyContent="space-between">
                {
                    showCloseButton && (
                        <Button
                            size="md"
                            colorScheme="muted"
                            variant="ghost"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    )
                }
                {
                    !showCloseButton && (
                        <Box />
                    )
                }
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={isPending || !isDirty}
                >
                    Next
                </Button>
            </ModalFooter>
        </VStack>
    );
}

export { EditPropertyScreen };
